import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Eye, AlertTriangle, Bell, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const STATUS = {
  drifting: { label: 'Drifting', color: C.red, Icon: TrendingUp },
  improving: { label: 'Improving', color: C.green, Icon: TrendingDown },
  steady: { label: 'Steady', color: C.slate, Icon: Minus },
};
const ndpColor = (v) => (v >= 60 ? C.red : v >= 40 ? C.amber : v >= 25 ? C.blue : C.green);

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

const Sparkline = ({ months }) => {
  if (!months || months.length < 2) return <span className="text-[10px]" style={{ color: C.slate }}>—</span>;
  const vals = months.map(m => m.avg_ndp);
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * 72},${20 - ((v - min) / range) * 16}`).join(' ');
  return (
    <svg width="76" height="24" className="shrink-0">
      <polyline points={pts} fill="none" stroke={ndpColor(vals[vals.length - 1])} strokeWidth="1.5" />
    </svg>
  );
};

export default function ConductTrendsPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [drift, setDrift] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [resolving, setResolving] = useState(null);

  const loadDrift = useCallback(() => {
    api.get('/dimension-u/drift').then(r => setDrift(r.data)).catch(() => setDrift({ open_escalations: [] }));
  }, [api]);
  useEffect(() => { loadDrift(); }, [loadDrift]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data: check } = await api.post('/dimension-u/drift/run');
      toast[check.new_escalations ? 'warning' : 'success'](check.new_escalations
        ? `ARIA escalated ${check.new_escalations} broker(s) to the governance feed`
        : `Sweep complete — ${check.brokers_reviewed} broker(s) reviewed, no new conduct escalations`);
      loadDrift();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sweep failed'); }
    setSweeping(false);
  };

  const resolveEscalation = async (esc) => {
    setResolving(esc.escalation_id);
    try {
      await api.post(`/governance/escalations/${esc.escalation_id}/resolve`,
        { note: 'Reviewed on Conduct Trends — discussed with brokerage' });
      toast.success(`${esc.reference} conduct escalation resolved`);
      loadDrift();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resolve failed'); }
    setResolving(null);
  };

  const load = useCallback(() => {
    api.get('/dimension-u/trends').then(r => {
      setData(r.data);
      if (r.data.brokers.length && !selected) setSelected(r.data.brokers[0].broker_id);
    }).catch(() => setData({ brokers: [], summary: {} }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) return;
    setDetail(null);
    api.get(`/dimension-u/broker/${selected}/trend`).then(r => setDetail(r.data)).catch(() => setDetail(null));
  }, [selected, api]);

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}</div>;
  const { brokers, summary } = data;

  return (
    <div className="space-y-5" data-testid="conduct-trends-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Conduct Trends</h2>
          <p className="text-sm mt-0.5" style={{ color: C.slate }}>
            Dimension U tracks each broker's real quote-editing behaviour. Rising non-disclosure scores surface here before renewal season — not after.
          </p>
        </div>
        <button onClick={runSweep} disabled={sweeping} data-testid="conduct-sweep-btn"
          className="flex items-center gap-1.5 px-3 py-2 rounded-sm border text-[11px] font-semibold transition-colors hover:bg-[#F5F7FA] disabled:opacity-50"
          style={{ color: C.blue, borderColor: 'rgba(26,76,124,0.3)' }}>
          {sweeping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
          Run ARIA sweep
        </button>
      </div>

      {drift && (drift.open_escalations || []).length > 0 && (
        <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(226,75,74,0.35)', borderLeft: '3px solid #E24B4A' }}
          data-testid="conduct-escalations-card">
          <div className="px-4 pt-3 pb-2 flex items-center gap-1.5">
            <Bell className="w-3.5 h-3.5" style={{ color: C.red }} />
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.red }}>
              {drift.open_escalations.length} conduct escalation{drift.open_escalations.length > 1 ? 's' : ''} awaiting review — posted by ARIA to the governance feed
            </h3>
          </div>
          {drift.open_escalations.map(esc => (
            <div key={esc.escalation_id} className="px-4 py-2.5 border-t flex items-center gap-3 flex-wrap" style={{ borderColor: 'rgba(136,150,168,0.1)' }}
              data-testid={`conduct-escalation-${esc.ref_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium" style={{ color: C.navy }}>{esc.reference}</p>
                <p className="text-[10px]" style={{ color: '#B03230' }}>{(esc.triggers || []).map(t => t.detail).join(' · ')}</p>
                <p className="text-[10px] italic" style={{ color: C.slate }}>{esc.recommendation}</p>
              </div>
              <button onClick={() => resolveEscalation(esc)} disabled={resolving === esc.escalation_id}
                data-testid={`resolve-conduct-${esc.ref_id}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: C.green }}>
                <CheckCircle className="w-3 h-3" />Mark reviewed
              </button>
            </div>
          ))}
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[['Brokers monitored', summary.monitored || 0, C.navy],
          ['Drifting', summary.drifting || 0, summary.drifting ? C.red : C.navy],
          ['Improving', summary.improving || 0, C.green],
          ['Panel avg NDP', summary.panel_avg_ndp || 0, ndpColor(summary.panel_avg_ndp || 0)]].map(([l, v, color]) => (
          <Card key={l} className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
            <Overline>{l}</Overline>
            <p className="text-3xl font-light tracking-tight mt-1" style={{ color, fontVariantNumeric: 'tabular-nums' }}>{v}</p>
          </Card>
        ))}
      </div>

      {brokers.length === 0 ? (
        <Card className="p-6 rounded-sm bg-white text-center" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="conduct-empty">
          <p className="text-sm" style={{ color: C.slate }}>
            No captured quote-editing behaviour yet. Signals appear automatically as brokers quote — every field they revise on the live form is tracked by Dimension U.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Broker panel table */}
          <Card className="lg:col-span-2 rounded-sm bg-white overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="conduct-broker-table">
            <div className="px-4 pt-3.5 pb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Panel behaviour ({summary.window_months || 6}-month window)</h3>
            </div>
            {brokers.map(b => {
              const cfg = STATUS[b.status];
              const active = selected === b.broker_id;
              return (
                <button key={b.broker_id} onClick={() => setSelected(b.broker_id)} data-testid={`conduct-broker-${b.broker_id}`}
                  className="w-full text-left px-4 py-2.5 border-t flex items-center gap-3 transition-colors hover:bg-[#F5F7FA]"
                  style={{ borderColor: 'rgba(136,150,168,0.1)', background: active ? 'rgba(26,76,124,0.05)' : undefined, borderLeft: active ? `3px solid ${C.blue}` : '3px solid transparent' }}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate" style={{ color: C.navy }}>{b.broker_name}</p>
                    <p className="text-[10px]" style={{ color: C.slate }}>{b.submissions} quotes · {b.downgrades} risk downgrades{b.auto_refers > 0 && ` · ${b.auto_refers} auto-refers`}</p>
                  </div>
                  <Sparkline months={b.months} />
                  <span className="text-xs font-mono font-semibold w-9 text-right" style={{ color: ndpColor(b.avg_ndp) }}>{b.avg_ndp}</span>
                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase w-[74px] justify-center"
                    style={{ background: `${cfg.color}12`, color: cfg.color }}>
                    <cfg.Icon className="w-2.5 h-2.5" />{b.drift != null ? `${b.drift > 0 ? '+' : ''}${b.drift}` : cfg.label}
                  </span>
                </button>
              );
            })}
            <p className="px-4 py-2.5 text-[10px] border-t" style={{ color: C.slate, borderColor: 'rgba(136,150,168,0.1)' }}>
              NDP = non-disclosure probability (0–100). Drift compares the recent half of the window with the earlier half — +5 or worse flags the broker.
            </p>
          </Card>

          {/* Drilldown */}
          <div className="lg:col-span-3 space-y-4">
            {!detail ? (
              <Skeleton className="h-64 rounded-sm" />
            ) : (
              <>
                <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="conduct-drilldown-chart">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div>
                      <Overline>Behaviour over time</Overline>
                      <p className="text-sm font-medium" style={{ color: C.navy }}>{detail.broker_name}</p>
                    </div>
                    <p className="text-[10px]" style={{ color: C.slate }}>
                      {detail.summary?.real_capture_records || 0} live-captured clients · {detail.summary?.session_edits_total || 0} session edits recorded
                    </p>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <ComposedChart data={detail.months} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.slate }} />
                      <YAxis yAxisId="ndp" domain={[0, 100]} tick={{ fontSize: 10, fill: C.slate }} />
                      <YAxis yAxisId="cnt" orientation="right" hide />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="cnt" dataKey="session_edits" name="Session edits" fill="rgba(26,76,124,0.25)" barSize={18} />
                      <Bar yAxisId="cnt" dataKey="downgrades" name="Risk downgrades" fill={C.red} barSize={18} />
                      <Line yAxisId="ndp" type="monotone" dataKey="avg_ndp" name="Avg NDP" stroke={C.teal} strokeWidth={2} dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="conduct-events">
                  <div className="px-4 pt-3.5 pb-2 flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" style={{ color: C.blue }} />
                    <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Flagged quote edits</h3>
                  </div>
                  {(detail.events || []).length === 0 ? (
                    <p className="px-4 pb-4 text-xs" style={{ color: C.slate }}>No flagged editing behaviour for this broker.</p>
                  ) : (
                    detail.events.map(e => (
                      <div key={e.submission_id} className="px-4 py-2.5 border-t flex items-start gap-2.5" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: e.downgrades.length ? C.red : C.amber }} />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs" style={{ color: C.navy }}>
                            <span className="font-medium">{e.business_name}</span>
                            <span className="capitalize" style={{ color: C.slate }}> · {String(e.insurance_class).replace(/_/g, ' ')} · {String(e.created_at).slice(0, 10)}</span>
                          </p>
                          <p className="text-[10px]" style={{ color: e.downgrades.length ? '#B03230' : C.slate }}>
                            {e.downgrades.length ? e.downgrades.join('; ') : (e.flags[0] || '')}
                          </p>
                        </div>
                        {e.ndp_score != null && (
                          <span className="text-xs font-mono font-semibold" style={{ color: ndpColor(e.ndp_score) }}>{e.ndp_score}</span>
                        )}
                      </div>
                    ))
                  )}
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
